import React, { useState, useEffect } from 'react'
import {
  Grid,
  Paper,
  Typography,
  Box,
  Card,
  CardContent,
  Button,
  Chip,
  Avatar,
  useTheme,
  Skeleton,
} from '@mui/material'
import {
  AccessTime as TimeIcon,
  CalendarToday as CalendarIcon,
  CheckCircle as CheckIcon,
  PendingActions as PendingIcon,
  TrendingUp as TrendIcon,
  ArrowForward as ArrowIcon,
  EventAvailable as EventIcon,
  AttachMoney as MoneyIcon,
  People as PeopleIcon,
  History as HistoryIcon,
  BeachAccess as LeaveIcon,
} from '@mui/icons-material'
import { useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar, Legend } from 'recharts'
import { useLoading } from '../../hooks'
import { StatusChip } from '../../components/common/Chips'
import { attendanceApi } from '../../api/attendanceApi'
import { dashboardApi } from '../../api/dashboardApi'
import { formatDate } from '../../utils/formatters'

const EmployeeDashboard = () => {
  const navigate = useNavigate()
  const theme = useTheme()
  const { user, orgId } = useSelector((state) => state.auth)
  const { loading, withLoading } = useLoading()


  const [dashboardData, setDashboardData] = useState({
    stats: {
      presentDays: 22,
      leavesTaken: 3,
      pendingLeaves: 2,
      attendancePercentage: 92,
      totalWorkingDays: 24,
      leavesRemaining: 12,
      lateArrivals: 2,
      overtimeHours: 8,
    },
    attendanceChart: [],
    todayStatus: {
      checkIn: 'Checking...',
      checkOut: 'Checking...',
      status: 'Pending',
      workHours: 0,
      isLate: false,
      overtime: 0,
      location: '-',
    },
    recentActivities: [
      { id: 1, time: 'Today, 9:00 AM', activity: 'Check-in marked', type: 'success' },
      { id: 2, time: 'Yesterday, 6:30 PM', activity: 'Check-out marked', type: 'success' },
      { id: 3, time: '2 days ago', activity: 'Leave request submitted', type: 'info' },
      { id: 4, time: '3 days ago', activity: 'Profile updated', type: 'info' },
      { id: 5, time: '1 week ago', activity: 'Salary credited', type: 'success' },
    ],
    upcomingLeaves: [],
    monthlyOverview: [],
  })

  useEffect(() => {
    if (user?.id && orgId) {
      fetchDashboardData()
    }
  }, [user, orgId])

  const fetchDashboardData = async () => {
    withLoading(async () => {
      try {
        const headers = {
          'X-Employee-ID': user?.id,
          'X-Org-ID': orgId
        }

        // Fetch today's attendance
        const todayResponse = await attendanceApi.getTodayAttendance(headers)
        if (todayResponse.data) {
          const data = todayResponse.data
          setDashboardData(prev => ({
            ...prev,
            todayStatus: {
              checkIn: data.check_in || '-',
              checkOut: data.check_out || '-',
              status: data.status || 'Pending',
              workHours: data.work_hours || 0,
              isLate: data.is_late || false,
              overtime: data.overtime || null,
              location: data.location || '-',
            }
          }))
        }

        // Fetch weekly attendance hours
        const weeklyResponse = await attendanceApi.getWeeklyAttendanceHours(headers)
        if (weeklyResponse.data) {
          const weeklyData = weeklyResponse.data
          const chartData = Object.entries(weeklyData).map(([day, hours]) => ({
            day,
            hours: hours || 0
          }))
          setDashboardData(prev => ({
            ...prev,
            attendanceChart: chartData
          }))
        }

        // Fetch upcoming leaves
        const leavesResponse = await dashboardApi.getUpcomingLeaves(user.id, orgId)
        if (leavesResponse.data) {
          const upcomingLeaves = leavesResponse.data.map((leave, index) => ({
            id: index,
            date: formatDate(leave.start_date),
            type: leave.leave_type,
            status: leave.status,
            days: leave.duration
          }))

          setDashboardData(prev => ({
            ...prev,
            upcomingLeaves: upcomingLeaves
          }))
        }

        // Fetch Monthly Attendance Overview
        const overviewResponse = await attendanceApi.getMonthlyAttendanceOverview({
          'X-Org-ID': orgId
        })

        if (overviewResponse.data) {
          const overviewData = overviewResponse.data
          const mappedMonthlyData = Object.entries(overviewData).map(([month, data]) => ({
            month,
            present: data.present_days || 0,
            absent: data.absent_days || 0,
            late: data.late_arrivals || 0
          }))

          setDashboardData(prev => ({
            ...prev,
            monthlyOverview: mappedMonthlyData
          }))
        }
      } catch (error) {
        console.error('Error fetching dashboard data:', error)
      }
    })
  }




  const getGreeting = () => {
    const hour = new Date().getHours()
    if (hour < 12) return 'Good Morning'
    if (hour < 18) return 'Good Afternoon'
    return 'Good Evening'
  }


  return (
    <Box sx={{ p: { xs: 1, md: 3 } }}>
      <Paper sx={{ p: { xs: 2, md: 3 }, borderRadius: 3, bgcolor: 'background.paper', boxShadow: 3 }}>
        <Box sx={{ mb: 4 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
            <Box>
              <Typography variant="h4" gutterBottom>
                {getGreeting()}, {user?.name || 'Employee'}
              </Typography>
              <Typography variant="body1" color="textSecondary" paragraph>
                Welcome back! Here's your activity summary for today
              </Typography>
            </Box>
          </Box>




        </Box>

        <Grid container spacing={3}>
          {/* Row 1: Today's Status */}
          <Grid size={{ xs: 12 }}>
            <Paper variant="outlined" sx={{ p: 3, height: '100%', backgroundColor: theme.palette.mode === 'dark' ? 'action.hover' : 'background.paper', display: 'flex', flexDirection: 'column' }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6">Today's Status</Typography>
                {loading ? <Skeleton width={100} height={32} /> : <StatusChip status={dashboardData.todayStatus.status} />}
              </Box>
              <Grid container spacing={2} sx={{ flexGrow: 1, alignContent: 'space-between' }}>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Typography variant="body2" color="textSecondary">Check-in</Typography>
                  {loading ? <Skeleton width={100} height={32} /> : <Typography variant="h6">{dashboardData.todayStatus.checkIn}</Typography>}
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Typography variant="body2" color="textSecondary">Check-out</Typography>
                  {loading ? <Skeleton width={100} height={32} /> : <Typography variant="h6">{dashboardData.todayStatus.checkOut}</Typography>}
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Typography variant="body2" color="textSecondary">Work Hours</Typography>
                  {loading ? <Skeleton width={80} height={32} /> : <Typography variant="h6">{dashboardData.todayStatus.workHours ? `${dashboardData.todayStatus.workHours} hrs` : '-'}</Typography>}
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Typography variant="body2" color="textSecondary">Overtime</Typography>
                  {loading ? <Skeleton width={80} height={32} /> : (
                    <Typography variant="h6" color={dashboardData.todayStatus.overtime > 0 ? "success.main" : "textSecondary"}>
                      {dashboardData.todayStatus.overtime !== null && dashboardData.todayStatus.overtime > 0
                        ? `+${dashboardData.todayStatus.overtime} hrs`
                        : '-'}
                    </Typography>
                  )}
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <Typography variant="body2" color="textSecondary">Location</Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {loading ? <Skeleton width={150} height={32} /> : <Typography variant="h6">{dashboardData.todayStatus.location}</Typography>}
                  </Box>
                </Grid>
              </Grid>
            </Paper>
          </Grid>
          {/* Row 2, Col 1: Weekly Attendance Hours */}
          <Grid size={{ xs: 12, md: 6 }}>
            <Paper variant="outlined" sx={{ p: 3, borderRadius: 2, height: '100%' }}>
              <Typography variant="h6" gutterBottom>
                Weekly Attendance Hours
              </Typography>
              <Box sx={{ height: 300, minHeight: 300 }}>
                {loading ? (
                  <Skeleton variant="rectangular" width="100%" height={300} sx={{ borderRadius: 2 }} />
                ) : (
                  <ResponsiveContainer width="100%" height={300}>
                    <LineChart data={dashboardData.attendanceChart}>
                      <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} />
                      <XAxis dataKey="day" stroke={theme.palette.text.secondary} />
                      <YAxis stroke={theme.palette.text.secondary} label={{ value: 'Hours', angle: -90, position: 'insideLeft', fill: theme.palette.text.secondary }} />
                      <Tooltip />
                      <Line
                        type="monotone"
                        dataKey="hours"
                        stroke={theme.palette.primary.main}
                        strokeWidth={2}
                        dot={{ r: 4 }}
                        activeDot={{ r: 6 }}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                )}
              </Box>
            </Paper>
          </Grid>

          {/* Row 2, Col 2: Monthly Attendance Overview */}
          <Grid size={{ xs: 12, md: 6 }}>
            <Paper variant="outlined" sx={{ p: 3, borderRadius: 2, height: '100%' }}>
              <Typography variant="h6" gutterBottom>
                Monthly Attendance Overview
              </Typography>
              <Box sx={{ height: 300, minHeight: 300 }}>
                {loading ? (
                  <Skeleton variant="rectangular" width="100%" height={300} sx={{ borderRadius: 2 }} />
                ) : (
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={dashboardData.monthlyOverview}>
                      <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} />
                      <XAxis dataKey="month" stroke={theme.palette.text.secondary} />
                      <YAxis stroke={theme.palette.text.secondary} />
                      <Tooltip />
                      <Legend />
                      <Bar dataKey="present" name="Present Days" fill="#4caf50" />
                      <Bar dataKey="absent" name="Absent Days" fill="#f44336" />
                      <Bar dataKey="late" name="Late Arrivals" fill="#ff9800" />
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </Box>
            </Paper>
          </Grid>

          {/* <Grid size={{ xs: 12, md: 6 }}>
            <Paper variant="outlined" sx={{ p: 3, borderRadius: 2, height: '100%' }}>
              <Typography variant="h6" gutterBottom>Recent Activity</Typography>
              <Box>
                {loading ? (
                  [1, 2, 3].map((i) => (
                    <Box key={i} sx={{ py: 1.5, borderBottom: `1px solid ${theme.palette.divider}` }}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Skeleton variant="circular" width={8} height={8} sx={{ mr: 2 }} />
                        <Box sx={{ flexGrow: 1 }}>
                          <Skeleton width="80%" height={20} />
                          <Skeleton width="40%" height={16} />
                        </Box>
                      </Box>
                    </Box>
                  ))
                ) : (
                  dashboardData.recentActivities.slice(0, 5).map((activity) => (
                    <Box
                      key={activity.id}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        py: 1.5,
                        borderBottom: `1px solid ${theme.palette.divider}`,
                        '&:last-child': { borderBottom: 'none' }
                      }}
                    >
                      <Box
                        sx={{
                          width: 8,
                          height: 8,
                          borderRadius: '50%',
                          backgroundColor: activity.type === 'success' ? 'success.main' : 'info.main',
                          mr: 2,
                        }}
                      />
                      <Box sx={{ flexGrow: 1 }}>
                        <Typography variant="body2">{activity.activity}</Typography>
                        <Typography variant="caption" color="textSecondary">{activity.time}</Typography>
                      </Box>
                    </Box>
                  ))
                )}
              </Box>
            </Paper>
          </Grid> */}

          {/* Row 3, Col 1: Upcoming Leaves */}
          <Grid size={{ xs: 12, md: 6 }}>
            <Paper variant="outlined" sx={{ p: 3, height: '100%' }}>
              <Typography variant="h6" gutterBottom>Upcoming Leaves</Typography>
              {loading ? (
                [1, 2].map((i) => (
                  <Box key={i} sx={{ py: 1.5, borderBottom: `1px solid ${theme.palette.divider}` }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Box sx={{ width: '60%' }}>
                        <Skeleton width="80%" height={24} />
                        <Skeleton width="40%" height={16} />
                      </Box>
                      <Skeleton width={80} height={24} sx={{ borderRadius: 1 }} />
                    </Box>
                  </Box>
                ))
              ) : (
                dashboardData.upcomingLeaves.length === 0 ? (
                  <Typography variant="body2" color="textSecondary" sx={{ py: 2, textAlign: 'center' }}>
                    No upcoming leaves
                  </Typography>
                ) : (
                  dashboardData.upcomingLeaves.map((leave, index) => (
                    <Box
                      key={leave.id || index}
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        py: 1.5,
                        borderBottom: `1px solid ${theme.palette.divider}`,
                        '&:last-child': { borderBottom: 'none' }
                      }}
                    >
                      <Box>
                        <Typography variant="body2">{leave.date}</Typography>
                        <Typography variant="caption" color="textSecondary">{leave.type} • {leave.days} day(s)</Typography>
                      </Box>
                      <StatusChip status={leave.status} />
                    </Box>
                  ))
                )
              )}
              <Button
                fullWidth
                variant="contained"
                sx={{ mt: 2 }}
                onClick={() => navigate('/employee/leave/apply')}
                endIcon={<ArrowIcon />}
              >
                Apply for Leave
              </Button>
            </Paper>
          </Grid>

          {/* Row 3, Col 2: Quick Actions */}
          <Grid size={{ xs: 12, md: 6 }}>
            <Paper variant="outlined" sx={{ p: 3, borderRadius: 2, height: '100%' }}>
              <Typography variant="h6" gutterBottom>
                Quick Actions
              </Typography>
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, md: 6 }}>
                  <Card
                    variant="outlined"
                    sx={{
                      borderRadius: 2,
                      cursor: 'pointer',
                      bgcolor: 'background.paper',
                      transition: '0.3s',
                      height: '100%',
                      '&:hover': {
                        bgcolor: '#e3f2fd',
                        borderColor: 'primary.main',
                        transform: 'translateY(-2px)',
                        boxShadow: 2,
                        '& .MuiTypography-root': { color: '#000000' },
                        '& .MuiTypography-caption': { color: 'rgba(0, 0, 0, 0.6)' }
                      }
                    }}
                    onClick={() => navigate('/employee/attendance')}
                  >
                    <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 2 }}>
                      <Avatar sx={{ bgcolor: 'primary.main', width: 40, height: 40 }}>
                        <TimeIcon fontSize="small" />
                      </Avatar>
                      <Box>
                        <Typography variant="subtitle1" fontWeight="bold" color="text.primary">
                          Mark Attendance
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Check-in or Check-out
                        </Typography>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>

                <Grid size={{ xs: 12, md: 6 }}>
                  <Card
                    variant="outlined"
                    sx={{
                      borderRadius: 2,
                      cursor: 'pointer',
                      bgcolor: 'background.paper',
                      transition: '0.3s',
                      height: '100%',
                      '&:hover': {
                        bgcolor: '#fce4ec',
                        borderColor: 'secondary.main',
                        transform: 'translateY(-2px)',
                        boxShadow: 2,
                        '& .MuiTypography-root': { color: '#000000' },
                        '& .MuiTypography-caption': { color: 'rgba(0, 0, 0, 0.6)' }
                      }
                    }}
                    onClick={() => navigate('/employee/leave/apply')}
                  >
                    <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 2 }}>
                      <Avatar sx={{ bgcolor: 'secondary.main', width: 40, height: 40 }}>
                        <EventIcon fontSize="small" />
                      </Avatar>
                      <Box>
                        <Typography variant="subtitle1" fontWeight="bold" color="text.primary">
                          Apply Leave
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Request time off
                        </Typography>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>

                <Grid size={{ xs: 12, md: 6 }}>
                  <Card
                    variant="outlined"
                    sx={{
                      borderRadius: 2,
                      cursor: 'pointer',
                      bgcolor: 'background.paper',
                      transition: '0.3s',
                      height: '100%',
                      '&:hover': {
                        bgcolor: '#e1f5fe',
                        borderColor: 'info.main',
                        transform: 'translateY(-2px)',
                        boxShadow: 2,
                        '& .MuiTypography-root': { color: '#000000' },
                        '& .MuiTypography-caption': { color: 'rgba(0, 0, 0, 0.6)' }
                      }
                    }}
                    onClick={() => navigate('/employee/attendance/history')}
                  >
                    <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 2 }}>
                      <Avatar sx={{ bgcolor: 'info.main', width: 40, height: 40 }}>
                        <HistoryIcon fontSize="small" />
                      </Avatar>
                      <Box>
                        <Typography variant="subtitle1" fontWeight="bold" color="text.primary">
                          Attendance History
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          View your past attendance
                        </Typography>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>

                <Grid size={{ xs: 12, md: 6 }}>
                  <Card
                    variant="outlined"
                    sx={{
                      borderRadius: 2,
                      cursor: 'pointer',
                      bgcolor: 'background.paper',
                      transition: '0.3s',
                      height: '100%',
                      '&:hover': {
                        bgcolor: '#e8f5e9',
                        borderColor: 'success.main',
                        transform: 'translateY(-2px)',
                        boxShadow: 2,
                        '& .MuiTypography-root': { color: '#000000' },
                        '& .MuiTypography-caption': { color: 'rgba(0, 0, 0, 0.6)' }
                      }
                    }}
                    onClick={() => navigate('/employee/leave/history')}
                  >
                    <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 2 }}>
                      <Avatar sx={{ bgcolor: 'success.main', width: 40, height: 40 }}>
                        <LeaveIcon fontSize="small" />
                      </Avatar>
                      <Box>
                        <Typography variant="subtitle1" fontWeight="bold" color="text.primary">
                          Leave History
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Track your leave status
                        </Typography>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            </Paper>
          </Grid>
        </Grid>
      </Paper>
    </Box>
  )
}

export default EmployeeDashboard